<i18n locale="en">
{
  "close": "Close"
}
</i18n>
<template>
  <OwnDialog
    v-model="isVisible"
    class="upsell-sheet-dialog"
    position="bottom"
    anchored
    data-testid="upsell-sheet"
    @cancel="onDismiss"
  >
    <div class="upsell-sheet">
      <div class="upsell-sheet__scroll">
        <UpsellTakeover />
      </div>
      <button
        class="upsell-sheet__close"
        type="button"
        :aria-label="t('close')"
        data-testid="upsell-sheet-close-button"
        @click="onDismiss"
      >
        <PhX :size="20" />
      </button>
      <UpsellUpgradeCta class="upsell-sheet__cta" @upgrade="emit('upgrade')" />
    </div>
  </OwnDialog>
</template>

<script lang="ts" setup>
import { PhX } from '@phosphor-icons/vue'
import { useI18n } from 'vue-i18n'

import { OwnDialog } from '@/ui/OwnDialog'

import UpsellTakeover from './UpsellTakeover.vue'
import UpsellUpgradeCta from './UpsellUpgradeCta.vue'

const emit = defineEmits<{
  upgrade: []
}>()

const isVisible = defineModel<boolean>()

const { t } = useI18n()

const onDismiss = (): void => {
  isVisible.value = false
}
</script>

<style lang="scss" scoped>
// The takeover content scrolls inside the sheet while the close button and
// upgrade CTA float on top of it, pinned to the card's corners.
.upsell-sheet-dialog {
  :deep(.own-dialog__card) {
    height: 82%;
    max-height: 82%;
    background-color: #0b0d0c;
  }

  :deep(.own-dialog__card-content) {
    overflow: hidden;
  }
}

.upsell-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background-color: #0b0d0c;

  // Film-grain across the entire sheet. The grain asset is black speckles in
  // the alpha channel, so it's inverted to read as light grain on the dark
  // surface; pointer-events stay off so the chrome underneath remains usable.
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background-image: url('../assets/grain.png');
    background-size: 182px 182px;
    filter: invert(1);
    opacity: 0.5;
  }

  &__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    color: #fff;
    background-color: rgb(255 255 255 / 8%);

    &:hover {
      background-color: rgb(255 255 255 / 14%);
    }
  }

  &__cta {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 1;
  }
}
</style>
